/**
 * DashboardLayout — student dashboard shell.
 * Uses PanelLayout with student-specific sidebar sections.
 */
import {
  LayoutDashboard,
  BookOpen,
  Award,
  User,
  FolderGit2,
  CreditCard,
  Briefcase,
  Code,
  BarChart3,
  MessagesSquare,
  Rocket,
  Sparkles,
  X,
  FileText,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import PanelLayout from './PanelSidebar'
import type { SidebarSection } from './PanelSidebar'
import { useAuth } from '@/stores/auth-context'

const studentSections: SidebarSection[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Learning',
    items: [
      { label: 'My Courses', href: '/dashboard/courses', icon: BookOpen },
      { label: 'Materials', href: '/dashboard/materials', icon: FileText },
      { label: 'Projects', href: '/dashboard/projects', icon: FolderGit2 },
      { label: 'Daily Tasks', href: '/dashboard/daily-tasks', icon: Code },
      { label: 'Progress Report', href: '/dashboard/progress-report', icon: BarChart3 },
    ],
  },
  {
    label: 'Achievements',
    items: [
      { label: 'Certificates', href: '/dashboard/certificates', icon: Award },
      { label: 'Opportunities', href: '/dashboard/opportunities', icon: Briefcase },
    ],
  },
  {
    label: 'Community',
    items: [
      { label: 'Doubt Sessions', href: '/dashboard/doubt-sessions', icon: MessagesSquare },
    ],
  },
  {
    label: 'Account',
    items: [
      { label: 'Profile', href: '/dashboard/profile', icon: User },
      { label: 'Upgrade', href: '/courses?upgrade=1', icon: Rocket },
      { label: 'Payments', href: '/dashboard/payments', icon: CreditCard },
    ],
  },
]

export default function DashboardLayout() {
  const { user } = useAuth()
  const welcomeKey = user ? `pythonlife_welcomed_${user.id}` : null
  const [showWelcome, setShowWelcome] = useState(false)

  const isNewStudent = !!user?.created_at &&
    Date.now() - new Date(user.created_at).getTime() <= 3 * 24 * 60 * 60 * 1000

  useEffect(() => {
    if (!user?.id || !welcomeKey || !isNewStudent) return
    if (!localStorage.getItem(welcomeKey)) {
      setShowWelcome(true)
    }
  }, [user?.id, welcomeKey, isNewStudent])

  const dismissWelcome = () => {
    if (welcomeKey) {
      localStorage.setItem(welcomeKey, 'true')
    }
    setShowWelcome(false)
  }

  return (
    <>
      <PanelLayout sections={studentSections} panelLabel="Student Dashboard" />
      {showWelcome && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={dismissWelcome} />
          <div className="relative z-10 w-full max-w-sm">
            <div
              className="rounded-2xl sm:rounded-3xl overflow-hidden border border-white/[0.1] text-center"
              style={{ background: 'linear-gradient(160deg, hsl(0 0% 11%) 0%, hsl(0 0% 8%) 100%)' }}
            >
              <div className="h-1 w-full bg-gradient-to-r from-transparent via-primary to-transparent" />
              <div className="px-6 sm:px-8 pt-8 sm:pt-10 pb-6 sm:pb-8">
                <button onClick={dismissWelcome} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors">
                  <X className="h-5 w-5" />
                </button>

                <div className="relative inline-flex items-center justify-center mb-5">
                  <div className="absolute w-16 sm:w-20 h-16 sm:h-20 rounded-full bg-primary/10 animate-ping opacity-20" />
                  <div className="absolute w-14 sm:w-18 h-14 sm:h-18 rounded-full bg-primary/10" />
                  <div className="relative w-14 sm:w-16 h-14 sm:h-16 rounded-full flex items-center justify-center border-2 border-primary/40 bg-primary/[0.08]">
                    <Sparkles className="h-7 sm:h-8 w-7 sm:w-8 text-primary" />
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-1" style={{ letterSpacing: '-0.03em' }}>
                  Welcome to
                </h2>
                <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-3" style={{ letterSpacing: '-0.03em' }}>
                  PythonLife!
                </h2>

                <p className="text-sm text-muted-foreground mb-2">
                  Hi <span className="text-foreground font-medium">{user?.full_name?.split(' ')[0] ?? 'Student'}</span>, we're thrilled to have you here.
                </p>
                <p className="text-xs text-muted-foreground/70 mb-6 leading-relaxed">
                  Explore courses, track your progress, and start your journey to becoming a Python expert.
                </p>

                <button
                  onClick={dismissWelcome}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm bg-primary transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
                  style={{ boxShadow: '0 8px 32px -8px rgba(224, 96, 36, 0.5)' }}
                >
                  Let's Go! →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
